import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles,
  Zap,
  Brain,
  FileText,
  CheckCircle,
  AlertCircle,
  Clock,
  TrendingUp,
  Upload,
  GitCompare,
  Building2,
} from "lucide-react";
import { cobGetStats, cobGetProcessStats, cobGetStatsV3 } from "../api";
import "./COBDashboard.css";

const COBDashboard = () => {
  const navigate = useNavigate();
  // Combined stats from v1, v2, v3 (Subscription Checker + Change Of Bank)
  const [extractStats, setExtractStats] = useState(null);
  // Process stats from v1, v2 (Subscription Process)
  const [processStats, setProcessStats] = useState(null);
  // V3-specific stats (Change Of Bank)
  const [v3Stats, setV3Stats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [extractData, processData, v3Data] = await Promise.all([
        cobGetStats(),  // Returns combined v1+v2+v3 stats
        cobGetProcessStats().catch(() => ({ 
          total_work_orders: 0, 
          v1_work_orders: 0, 
          v2_work_orders: 0,
          completed: 0,
          pending: 0,
          processing: 0,
          failed: 0
        })),
        cobGetStatsV3().catch(() => ({ 
          total_work_orders: 0, 
          completed: 0, 
          pending: 0,
          processing: 0,
          failed: 0 
        }))
      ]);
      setExtractStats(extractData);
      setProcessStats(processData);
      setV3Stats(v3Data);
    } catch (err) {
      console.error("Failed to fetch COB stats:", err);
    } finally {
      setLoading(false);
    }
  };

  // Subscription Checker cards (V1 & V2 - OCR extraction only)
  const extractCards = [
    {
      id: "v1-extract",
      name: "Subscription Checker V1",
      description: "Google Gemini",
      icon: Sparkles,
      color: "#4285f4",
      bgColor: "#eff6ff",
      path: "/cob/v1-workorders",
      total: extractStats?.v1_work_orders || 0,
      type: "Extract",
    },
    {
      id: "v2-extract",
      name: "Subscription Checker V2",
      description: "Qwen VL",
      icon: Zap,
      color: "#059669",
      bgColor: "#ecfdf5",
      path: "/cob/v2-workorders",
      total: extractStats?.v2_work_orders || 0,
      type: "Extract",
    },
  ];

  // Subscription Process cards (V1 & V2 - Full pipeline)
  const processCards = [
    {
      id: "v1-process",
      name: "Subscription Process V1",
      description: "Gemini - Full Pipeline",
      icon: GitCompare,
      color: "#0891b2",
      bgColor: "#ecfeff",
      path: "/cob/v1-process-workorders",
      total: processStats?.v1_work_orders || 0,
      type: "Process",
    },
    {
      id: "v2-process",
      name: "Subscription Process V2",
      description: "Qwen - Full Pipeline",
      icon: GitCompare,
      color: "#dc2626",
      bgColor: "#fef2f2",
      path: "/cob/v2-process-workorders",
      total: processStats?.v2_work_orders || 0,
      type: "Process",
    },
  ];

  // Calculate totals for overview
  // Note: extractStats includes v1+v2+v3 combined stats, but v3 is separate as "Change Of Bank"
  // So for overview, we use extractStats (v1+v2 subscription checker only) + processStats + v3Stats
  const subscriptionCheckerTotal = (extractStats?.v1_work_orders || 0) + (extractStats?.v2_work_orders || 0);
  const changeOfBankTotal = v3Stats?.total_work_orders || 0;
  const subscriptionProcessTotal = processStats?.total_work_orders || 0;
  
  const totalWorkOrders = subscriptionCheckerTotal + changeOfBankTotal + subscriptionProcessTotal;
  
  // For completed/pending/failed, we need to calculate carefully
  // extractStats.completed includes v1+v2+v3 combined, so subtract v3 to get v1+v2 only
  const subscriptionCheckerCompleted = (extractStats?.completed || 0) - (v3Stats?.completed || 0);
  const subscriptionCheckerPending = (extractStats?.pending || 0) - (v3Stats?.pending || 0);
  const subscriptionCheckerFailed = (extractStats?.failed || 0) - (v3Stats?.failed || 0);
  
  const totalCompleted = subscriptionCheckerCompleted + (v3Stats?.completed || 0) + (processStats?.completed || 0);
  const totalPending = subscriptionCheckerPending + (v3Stats?.pending || 0) + (processStats?.pending || 0);
  const totalFailed = subscriptionCheckerFailed + (v3Stats?.failed || 0) + (processStats?.failed || 0);

  return (
    <div className="cob-dashboard">
      {/* Welcome Banner */}
      <div className="cob-welcome-banner">
        <div className="cob-welcome-content">
          <h1>🏦 Change of Bank Pipeline</h1>
          <p>Extract cheque details and run full comparison pipeline</p>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="cob-stats-overview">
        <div className="cob-stat-card total">
          <div className="cob-stat-icon">
            <FileText size={24} />
          </div>
          <div className="cob-stat-info">
            <span className="cob-stat-value">
              {loading ? "-" : totalWorkOrders}
            </span>
            <span className="cob-stat-label">Total Work Orders</span>
          </div>
        </div>

        <div className="cob-stat-card completed">
          <div className="cob-stat-icon">
            <CheckCircle size={24} />
          </div>
          <div className="cob-stat-info">
            <span className="cob-stat-value">
              {loading ? "-" : totalCompleted}
            </span>
            <span className="cob-stat-label">Completed</span>
          </div>
        </div>

        <div className="cob-stat-card pending">
          <div className="cob-stat-icon">
            <Clock size={24} />
          </div>
          <div className="cob-stat-info">
            <span className="cob-stat-value">
              {loading ? "-" : totalPending}
            </span>
            <span className="cob-stat-label">Pending</span>
          </div>
        </div>

        <div className="cob-stat-card failed">
          <div className="cob-stat-icon">
            <AlertCircle size={24} />
          </div>
          <div className="cob-stat-info">
            <span className="cob-stat-value">
              {loading ? "-" : totalFailed}
            </span>
            <span className="cob-stat-label">Failed</span>
          </div>
        </div>
      </div>

      {/* ============ SUBSCRIPTION SECTION ============ */}
      
      {/* Subscription Checker Cards (OCR Only) */}
      <h2 className="cob-section-title">
        <TrendingUp size={20} />
        Subscription Checker (OCR Only)
      </h2>

      <div className="cob-model-cards">
        {extractCards.map((model) => (
          <div
            key={model.id}
            className="cob-model-card"
            style={{ "--model-color": model.color, "--model-bg": model.bgColor }}
            onClick={() => navigate(model.path)}
          >
            <div className="cob-model-card-header">
              <div className="cob-model-icon" style={{ backgroundColor: model.bgColor }}>
                <model.icon size={28} style={{ color: model.color }} />
              </div>
              <div className="cob-model-info">
                <h3>{model.name}</h3>
                <p>{model.description}</p>
              </div>
            </div>

            <div className="cob-model-card-stats">
              <div className="cob-model-stat">
                <span className="cob-model-stat-value">{model.total}</span>
                <span className="cob-model-stat-label">Work Orders</span>
              </div>
            </div>

            <div className="cob-model-card-action">
              <button>
                <Upload size={16} />
                View Work Orders
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Subscription Process Cards (Full Pipeline) */}
      <h2 className="cob-section-title">
        <GitCompare size={20} />
        Subscription Process (OCR → Merge → Validate → Compare)
      </h2>

      <div className="cob-model-cards">
        {processCards.map((model) => (
          <div
            key={model.id}
            className="cob-model-card"
            style={{ "--model-color": model.color, "--model-bg": model.bgColor }}
            onClick={() => navigate(model.path)}
          >
            <div className="cob-model-card-header">
              <div className="cob-model-icon" style={{ backgroundColor: model.bgColor }}>
                <model.icon size={28} style={{ color: model.color }} />
              </div>
              <div className="cob-model-info">
                <h3>{model.name}</h3>
                <p>{model.description}</p>
              </div>
            </div>

            <div className="cob-model-card-stats">
              <div className="cob-model-stat">
                <span className="cob-model-stat-value">{model.total}</span>
                <span className="cob-model-stat-label">Work Orders</span>
              </div>
            </div>

            <div className="cob-model-card-action">
              <button>
                <GitCompare size={16} />
                View Work Orders
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* ============ CHANGE OF BANK SECTION ============ */}
      
      {/* Change Of Bank Card */}
      <h2 className="cob-section-title">
        <Building2 size={20} />
        Change Of Bank (Full-Page Analysis)
      </h2>

      <div className="cob-model-cards">
        <div
          className="cob-model-card"
          style={{ "--model-color": "#7c3aed", "--model-bg": "#f3e8ff" }}
          onClick={() => navigate('/cob/v3-workorders')}
        >
          <div className="cob-model-card-header">
            <div className="cob-model-icon" style={{ backgroundColor: "#f3e8ff" }}>
              <Brain size={28} style={{ color: "#7c3aed" }} />
            </div>
            <div className="cob-model-info">
              <h3>Change Of Bank</h3>
              <p>Qwen VL - Full-Page Analysis</p>
            </div>
          </div>

          <div className="cob-model-card-stats">
            <div className="cob-model-stat">
              <span className="cob-model-stat-value">{v3Stats?.total_work_orders || 0}</span>
              <span className="cob-model-stat-label">Work Orders</span>
            </div>
          </div>

          <div className="cob-model-card-action">
            <button>
              <Upload size={16} />
              View Work Orders
            </button>
          </div>
        </div>
      </div>

      {/* Quick Info */}
      <div className="cob-quick-info">
        <div className="cob-info-card">
          <h4>Subscription Checker</h4>
          <p>OCR extraction only. Outputs cheque details from uploaded documents without comparison.</p>
        </div>
        <div className="cob-info-card">
          <h4>Subscription Process</h4>
          <p>Full pipeline: OCR → Merge with original XML → Validate fields → Compare differences.</p>
        </div>
        <div className="cob-info-card">
          <h4>Change Of Bank</h4>
          <p>Full-page analysis using AWS Bedrock. Supports any AMC layout for standalone COB forms.</p>
        </div>
        <div className="cob-info-card">
          <h4>API Docs</h4>
          <p>
            <a href="/api/extraction/cob/v1/docs" target="_blank" rel="noreferrer" style={{color: '#4285f4'}}>V1 Swagger</a>
            {' | '}
            <a href="/api/extraction/cob/v2/docs" target="_blank" rel="noreferrer" style={{color: '#059669'}}>V2 Swagger</a>
            {' | '}
            <a href="/api/extraction/cob/v3/docs" target="_blank" rel="noreferrer" style={{color: '#7c3aed'}}>V3 (COB) Swagger</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default COBDashboard;
